import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import OutletSearch from "../SelectSearch/OutletSearch";
import Navbar from "../Navbar";
import Footer from "../Footer";
import CreatableSelect from "react-select/creatable";
import { useStock } from "../../context/StockProvider";
import { useNavigate } from "react-router-dom";
import PurchaseItemForm from "./PurchaseEntry/PurchaseItemForm";
import PurchaseItemTable from "./PurchaseEntry/PurchaseItemTable";
import PurchaseSummarySection from "./PurchaseEntry/PurchaseSummarySection";
import ConfirmModal from "../Modal/ConfirmModal";

const STOCK_TYPE_OPTIONS = [
    { value: "Stockable", label: "Stockable" },
    { value: "Non-Stockable", label: "Non-Stockable" },
];

const DEPARTMENT_OPTIONS = [
    { value: "Beverage", label: "Beverage" },
    { value: "Food", label: "Food" },
];

const INITIAL_ITEM = {
    ItemName: "",
    BrandName: "",
    quantity: "",
    primary_unit: "",
    secondary_unit: "",
    Rate: "",
    Department: "",
    GroupName: "",
    stocktype: "",
    taxable: false,
    exp_date: "",
    isNew: false,
};

const formatSalesValue = (val) =>
    Number(val || 0).toLocaleString("en-NP", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const mergePurchaseItems = (items, newItem, editingIndex = null) => {
    const normalize = (v) => v ?? "";

    const isSameItem = (a, b) => {
        return (
            normalize(a.ItemName) === normalize(b.ItemName) &&
            normalize(a.Rate) === normalize(b.Rate) &&
            normalize(a.taxable) === normalize(b.taxable) &&
            normalize(a.stocktype) === normalize(b.stocktype) &&
            normalize(a.primary_unit) === normalize(b.primary_unit) &&
            normalize(a.Department) === normalize(b.Department)
        );
    };

    let updated = [...items];

    // 👉 If editing, replace first
    if (editingIndex !== null) {
        updated[editingIndex] = newItem;
    }

    // 👉 Find match (excluding self if editing)
    const matchIndex = updated.findIndex((item, i) => {
        if (editingIndex !== null && i === editingIndex) return false;
        return isSameItem(item, newItem);
    });

    if (matchIndex !== -1) {
        const target = updated[matchIndex];

        const newQty = target.quantity + newItem.quantity;
        const newSubtotal = newQty * target.Rate;
        const newVat = target.taxable ? newSubtotal * 0.13 : 0;

        updated[matchIndex] = {
            ...target,
            quantity: newQty,
            subtotal: newSubtotal,
            vat: newVat,
            total: newSubtotal + newVat
        };

        // 👉 Remove duplicate row
        if (editingIndex !== null) {
            updated.splice(editingIndex, 1);
        } else {
            // adding mode → just don't push new item
        }

        return updated;
    }

    // 👉 No match found
    if (editingIndex !== null) {
        return updated;
    }

    return [...updated, newItem];
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "34px",
        height: "34px",
        fontSize: "0.875rem",
        borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
        boxShadow: "none",
        "&:hover": { borderColor: "#3b82f6" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 6px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: "34px" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 6px" }),
    clearIndicator: (base) => ({ ...base, padding: "0 6px" }),
    menu: (base) => ({ ...base, zIndex: 50, fontSize: "0.875rem" }),
    option: (base, { isFocused }) => ({
        ...base,
        backgroundColor: isFocused ? "#EFF6FF" : "white",
        color: "#111827",
    }),
};

const readonlyClass = "col-span-2 w-full px-2 py-1 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed";
const editableClass = "col-span-2 w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500";

const PurchaseEntry = () => {
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [error, setError] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    // editingIndex: null = adding new, number = editing that row
    const [editingIndex, setEditingIndex] = useState(null);

    const token = localStorage.getItem("token");
    const navigate = useNavigate();

    const [companyName, setCompanyName] = useState("");
    const [orderedDate, setOrderedDate] = useState("");

    const [showReceiveModal, setShowReceiveModal] = useState(false);
    const [receiveResolver, setReceiveResolver] = useState(null);

    const { stockItems, stockLoading, stockError } = useStock();

    const [currentItem, setCurrentItem] = useState(INITIAL_ITEM);
    const [tableItems, setTableItems] = useState([]);

    const [companyOptions, setCompanyOptions] = useState([]);
    const [companyLoading, setCompanyLoading] = useState(false);

    const baseUrl = process.env.REACT_APP_BASE_URL;

    const itemOptions = stockItems.map(item => ({
        value: item.ItemName,
        label: item.ItemName,
        data: item,
    }));

    const backendDepartments = stockItems
        .map(item => item.Department)
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .map(dep => ({ value: dep, label: dep }));

    const departmentOptions = [
        ...DEPARTMENT_OPTIONS,
        ...backendDepartments.filter(
            bd => !DEPARTMENT_OPTIONS.find(d => d.value.toLowerCase() === bd.value.toLowerCase())
        ),
    ];

    // true when an existing stock item is selected (not newly created)
    const isExistingItem = currentItem.ItemName && !currentItem.isNew;

    useEffect(() => {
        if (!selectedOutlet) return;

        const fetchCompanies = async () => {
            try {
                setCompanyLoading(true);

                const res = await axios.post(`${baseUrl}/getCompanies`, {
                    outlet_name: selectedOutlet
                });

                const formatted = (res.data?.companies || []).map((name) => ({
                    value: name,
                    label: name,
                }));

                setCompanyOptions(formatted);
            } catch (err) {
                console.error("Failed to fetch companies", err);
            } finally {
                setCompanyLoading(false);
            }
        };

        fetchCompanies();
        clearForm();
    }, [baseUrl, selectedOutlet]);

    const handleCancel = () => {
        setCurrentItem(INITIAL_ITEM);
        setEditingIndex(null);
        setError(null);
    };

    const handleClearAll = () => {
        setTableItems([]);
        setCurrentItem(INITIAL_ITEM);
        setEditingIndex(null);
        setError(null);
    };

    const handleEditItem = (index) => {
        setCurrentItem({ ...tableItems[index] });
        setEditingIndex(index);
        setError(null);
    };

    const handleAddItem = () => {
        if (!currentItem.ItemName || !currentItem.quantity || !currentItem.Rate) {
            setError("Please fill in Item Name, Quantity, and Rate");
            return;
        }

        if (!currentItem.stocktype) {
            setError("Please select a Stock Type");
            return;
        }

        const qty = parseFloat(currentItem.quantity);
        const rate = parseFloat(currentItem.Rate);

        if (isNaN(qty) || isNaN(rate)) {
            setError("Invalid quantity or rate");
            return;
        }

        const subtotal = qty * rate;
        const vat = currentItem.taxable ? subtotal * 0.13 : 0;

        const built = {
            ...currentItem,
            quantity: qty,
            Rate: rate,
            subtotal,
            vat,
            total: subtotal + vat
        };

        setTableItems(prev =>
            mergePurchaseItems(prev, built, editingIndex)
        );

        setEditingIndex(null);
        setCurrentItem(INITIAL_ITEM);
        setError(null);
    };

    const handleRemoveItem = (index) => {
        setTableItems(prev => prev.filter((_, i) => i !== index));
        // if we were editing this row, cancel the edit
        if (editingIndex === index) {
            setCurrentItem(INITIAL_ITEM);
            setEditingIndex(null);
        }
    };

    const subTotal = tableItems.reduce((sum, i) => sum + i.subtotal, 0);
    const totalVat = tableItems.reduce((sum, i) => sum + i.vat, 0);
    const grandTotal = subTotal + totalVat;

    const isExistingWithExpiry = isExistingItem && currentItem.exp_date;
    const isExistingWithoutExpiry = isExistingItem && !currentItem.exp_date;

    const isExpiryDisabled =
        isExistingItem || (!currentItem.isNew && currentItem.stocktype !== "Stockable");

    const isExpiryChecked =
        isExistingWithExpiry
            ? true
            : isExistingWithoutExpiry
                ? false
                : !!currentItem.exp_date;


    const buildReqPayload = () => ({
        RequisitionType: "Purchase Order",
        Date: orderedDate,
        TotalAmount: grandTotal,
        TaxAmount: totalVat,
        Company_Name: companyName || "",
        purchaseBillNumber: null,
        DiscountAmount: null,
        Outlet_Name: selectedOutlet || "",
        purchase_id_ocular: null,
        purchase_from: "web",
        company_pan: "",
        payment_mode: "",

        RequisitionDetailsList: tableItems.map(item => ({
            ItemID: item.ItemID || null,
            UnitsOrdered: item.quantity,
            Rate: item.Rate,
            Name: item.ItemName || "",
            BrandName: item.BrandName || "",
            Code: item.Code || "",
            UOM: item.primary_unit || "",
            StockType: item.stocktype || "",
            Department: item.Department || "",
            GroupName: item.GroupName || "",
            ExpDate: item.exp_date || null,
            Status: "In Stock",
            Taxable: item.taxable ? "Yes" : "No"
        })),
    });

    const clearForm = () => {
        setTableItems([]);
        setCompanyName("");
        setOrderedDate("");
        setCurrentItem(INITIAL_ITEM);
        setEditingIndex(null);
    };

    const validateForm = () => {
        if (tableItems.length === 0) return "Add at least one item before submitting.";
        if (!selectedOutlet) return "Please select an outlet.";
        if (!companyName) return "Please select a company.";
        return null;
    };

    const insertNewItems = async () => {
        const newItems = tableItems.filter(item => item.isNew);
        if (newItems.length === 0) return;

        await axios.post(`${baseUrl}/checkAndInsertStockItems`, {
            items: newItems.map(item => ({
                ItemName: item.ItemName || "",
                OutletName: selectedOutlet || "",
                GroupName: item.GroupName || "",
                BrandName: item.BrandName || "",
                UOM: item.primary_unit || "",
                CurrentLevel: item.quantity,
                Rate: item.Rate,
                Total: item.subtotal,
                Type: item.Department || "",
                vendor_name: companyName || "",
                StockType: item.stocktype || "",
                exp_date: item.exp_date || null,
                taxable: item.taxable ? 1 : 0,
                primary_unit: item.primary_unit || "",
                secondary_unit: item.secondary_unit || "",
            })),
        });
    };

    const confirmReceive = () => {
        return new Promise((resolve) => {
            setReceiveResolver(() => resolve);
            setShowReceiveModal(true);
        });
    };

    const handleReceiveConfirm = () => {
        if (receiveResolver) receiveResolver(true);
        setShowReceiveModal(false);
        setReceiveResolver(null);
    };

    const handleReceiveCancel = () => {
        if (receiveResolver) receiveResolver(false);
        setShowReceiveModal(false);
        setReceiveResolver(null);
    };

    const handleModalClose = () => {
        setShowReceiveModal(false);
    };

    const handleSubmit = async () => {
        if (submitLoading) return;

        setSubmitError(null);

        const errorMsg = validateForm();
        if (errorMsg) {
            setSubmitError(errorMsg);
            toast.error(errorMsg);
            return;
        }

        const shouldReceive = await confirmReceive();

        if (shouldReceive === null) return;

        setSubmitLoading(true);

        try {
            await insertNewItems();

            if (shouldReceive) {
                const response = await axios.post(`${baseUrl}/ordered_purchase`, buildReqPayload());

                const orderID = response.data.ordered_purchase_id;
                toast.success("Purchase order created successfully!");

                if (orderID) {
                    setTimeout(() => navigate("/receive-purchase", {
                        state: { tableItems, selectedOutlet, companyName, reqID: orderID, newPurchase: true, orderedDate },
                    }), 1000)
                }
            } else {
                await axios.post(`${baseUrl}/ordered_purchase`, buildReqPayload());
                toast.success("Purchase order saved successfully!");
            }

            clearForm();
        } catch (err) {
            toast.error("Submission failed.");
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Navbar />

            <div className="flex-grow p-2">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white rounded-lg shadow-md p-4 mb-2">
                        <h2 className="text-lg font-semibold text-gray-800 mb-2">Purchase Entry</h2>

                        <div className="mb-4">
                            <OutletSearch selectedOutlet={selectedOutlet} setSelectedOutlet={setSelectedOutlet} token={token} />
                        </div>

                        {error && (
                            <div className="mb-3 py-2 px-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm max-w-fit">
                                {error}
                            </div>
                        )}
                        {stockError && (
                            <div className="mb-3 py-2 px-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm max-w-fit">
                                {stockError}
                            </div>
                        )}

                        {stockLoading && (
                            <div className="text-center py-4">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                                <p className="mt-2 text-gray-600 text-sm">Loading items...</p>
                            </div>
                        )}

                        {!stockLoading && !stockError && (
                            <>
                                {/* Top bar: item select + taxable */}
                                <div className="flex gap-8 items-end mb-3">
                                    <div className="flex items-center gap-3">
                                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                            Select Item
                                        </label>
                                        <div className="w-72">
                                            <CreatableSelect
                                                options={itemOptions}
                                                value={currentItem.ItemName ? { label: currentItem.ItemName, value: currentItem.ItemName } : null}
                                                onChange={(selected) => {
                                                    if (!selected) { setCurrentItem(INITIAL_ITEM); return; }
                                                    const item = selected.data;
                                                    if (item) {
                                                        setCurrentItem({
                                                            ItemName: item.ItemName,
                                                            BrandName: item.BrandName || "",
                                                            quantity: "",
                                                            primary_unit: item.UOM || item.primary_unit || "",
                                                            secondary_unit: item.secondary_unit || "",
                                                            Rate: item.Rate || "",
                                                            Department: item.Department || "",
                                                            GroupName: item.GroupName || "",
                                                            stocktype: item.stocktype || "",
                                                            taxable: item.taxable || false,
                                                            exp_date: item.exp_date || null,
                                                            isNew: false,
                                                        });
                                                    } else {
                                                        setCurrentItem(prev => ({ ...prev, ItemName: selected.value, isNew: false }));
                                                    }
                                                }}
                                                onCreateOption={(inputValue) => {
                                                    setCurrentItem({ ...INITIAL_ITEM, ItemName: inputValue, isNew: true });
                                                }}
                                                placeholder="Search or create item..."
                                                isClearable
                                                styles={selectStyles}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700">Taxable</label>
                                        <input
                                            type="checkbox"
                                            checked={currentItem.taxable}
                                            onChange={(e) => setCurrentItem({ ...currentItem, taxable: e.target.checked })}
                                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">13% VAT</span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700">Expiry</label>
                                        <input
                                            type="checkbox"
                                            checked={isExpiryChecked}
                                            disabled={isExpiryDisabled}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;

                                                setCurrentItem({
                                                    ...currentItem,
                                                    exp_date: isChecked
                                                        ? new Date().toLocaleDateString("en-CA")
                                                        : null,
                                                });
                                            }}
                                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                        />
                                    </div>
                                </div>

                                {/* Form + Table side by side */}
                                <div className="flex gap-4">
                                    {/* Left: item form */}
                                    <PurchaseItemForm
                                        currentItem={currentItem}
                                        setCurrentItem={setCurrentItem}
                                        isExistingItem={isExistingItem}
                                        editableClass={editableClass}
                                        readonlyClass={readonlyClass}
                                        departmentOptions={departmentOptions}
                                        STOCK_TYPE_OPTIONS={STOCK_TYPE_OPTIONS}
                                        error={error}
                                        handleAddItem={handleAddItem}
                                        handleCancel={handleCancel}
                                        handleClearAll={handleClearAll}
                                        tableItems={tableItems}
                                        editingIndex={editingIndex}
                                    />

                                    {/* Right: table */}
                                    <div className="w-2/3">
                                        {tableItems.length > 0 ? (
                                            <PurchaseItemTable
                                                tableItems={tableItems}
                                                editingIndex={editingIndex}
                                                handleEditItem={handleEditItem}
                                                handleRemoveItem={handleRemoveItem}
                                                formatSalesValue={formatSalesValue}
                                            />
                                        ) : (
                                            <div className="text-sm bg-white rounded-lg shadow-md p-12 text-center h-full flex items-center justify-center">
                                                <p className="text-gray-400">No items added yet.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Summary section */}
                    <PurchaseSummarySection
                        tableItems={tableItems}

                        companyOptions={companyOptions}
                        companyName={companyName}
                        setCompanyName={setCompanyName}
                        companyLoading={companyLoading}
                        selectStyles={selectStyles}
                        orderedDate={orderedDate}
                        setOrderedDate={setOrderedDate}

                        subTotal={subTotal}
                        totalVat={totalVat}
                        grandTotal={grandTotal}
                        formatSalesValue={formatSalesValue}

                        submitError={submitError}
                        handleSubmit={handleSubmit}
                        submitLoading={submitLoading}
                    />
                </div>

                <ConfirmModal
                    isOpen={showReceiveModal}
                    title="Receive Purchase Order"
                    message="Do you want to receive this purchase order now?"
                    confirmText="Yes, Receive"
                    cancelText="No, Save Only"
                    onConfirm={handleReceiveConfirm}
                    onCancel={handleReceiveCancel}
                    onClose={handleModalClose}
                    danger={false}
                />
            </div>

            <Footer />
        </div>
    );
};

export default PurchaseEntry;